import React, { useState, useEffect, useRef } from "react";
import { Dropdown } from "react-bootstrap";
import FilterDropdownRow from "../filter-dropdown-row/filter-dropdown-row";
import { cloneDeep } from "lodash";
import { formatDateToString } from "../../util/time-helpers";
import { getNumFilters } from "../../util/array-helpers";
import "./filter-button.scss";

const FilterButton = ({
    dateFormatID,
    filterData: {
        appliedFilters,
        conjunctionSelection,
        totalFilterOptions
    },
    updateFiltersApplied,
    updateConjunctionSelection,
    updateTotalFilterOptions,
    clearFilters,
    customDropdownToggleSelector = null,
    customDropdownItemSelector = null,
    headerText,
    pageIdentifier
}) => {
    const [isFiltering, setIsFiltering] = useState(false);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [focusedElement, setFocusedElement] = useState("");

    const filterButtonRef = useRef(null);

    useEffect(() => {
        if (appliedFilters.length > 0) {
            setIsFiltering(true);
            return;
        }
        setIsFiltering(false);
    }, [appliedFilters.length]);

    useEffect(() => {
        document.addEventListener("mousedown", handleDropdownOpen);
        return () => {
            document.removeEventListener("mousedown", handleDropdownOpen);
        };
    }, []);

    const handleDropdownOpen = (event) => {
        if (filterButtonRef.current.contains(event.target)) {
            setIsDropdownOpen(true);
            return;
        }
        setIsDropdownOpen(false);
    }

    //Adds a row of dropdowns so the user can select their filter
    const addFilterRow = (filterData, index) => {
        const filterDropdownItems = filterData.dropdownItems;
        const filterDropdownItems2 = filterData.dropdownItems2;

        let newTotalFilterOptions = cloneDeep(totalFilterOptions);
        newTotalFilterOptions[index].isAdded = true;

        //Apply the filter to the filter assigned state
        const newAppliedFilterData = {
            ...filterData,
            unSelectedItems: filterDropdownItems,
            unSelectedItems2: filterDropdownItems2,
            inclusionSelection: { name: "Includes", id: 0 },
            filterSelection: [],
            filterSelection2: []
        };
        delete newAppliedFilterData.dropdownItems;

        const newFilterOptionsApplied = [...appliedFilters, newAppliedFilterData];

        updateFiltersApplied(newFilterOptionsApplied);
        updateTotalFilterOptions(newTotalFilterOptions);
    }

    //filter type is the overall filter category (e.g projects, priority, etc.)
    //dataType is the type of data being updated (main filter or inclusion option)
    const updateFilterSelections = (filterType, updatedSelectedItems, updatedUnSelectedItems) => {
        let newFilterOptionsApplied = cloneDeep(appliedFilters);
        let updatedSelectionsTemp = updatedSelectedItems;

        const filterOptionIndex = appliedFilters.findIndex((elem) => elem.filterType === filterType);

        if (filterOptionIndex === -1) return;
        if (!updatedSelectedItems && !newFilterOptionsApplied[filterOptionIndex]["usesDatePicker"]) return;

        //Must be formatted in an array for the datepicker
        if (newFilterOptionsApplied[filterOptionIndex]["usesDatePicker"] === true) {
            updatedSelectionsTemp = formatDateToString(updatedSelectedItems);
        }

        newFilterOptionsApplied[filterOptionIndex].filterSelection = updatedSelectionsTemp;
        newFilterOptionsApplied[filterOptionIndex].unSelectedItems = updatedUnSelectedItems;
        updateFiltersApplied(newFilterOptionsApplied);
    }

    const updateFilterSelections2 = (filterType2, updatedSelectedItems2, updatedUnSelectedItems2) => {
        let newFilterOptionsApplied2 = cloneDeep(appliedFilters);
        let updatedSelectionsTemp2 = updatedSelectedItems2;

        const filterOptionIndex2 = appliedFilters.findIndex((elem2) => elem2.filterType2 === filterType2);

        if (filterOptionIndex2 === -1) return;
        if (!updatedSelectedItems2 && !newFilterOptionsApplied2[filterOptionIndex2]["usesDatePicker"]) return;

        //Must be formatted in an array for the datepicker
        if (newFilterOptionsApplied2[filterOptionIndex2]["usesDatePicker"] === true) {
            updatedSelectionsTemp2 = formatDateToString(updatedSelectedItems2);
        }

        newFilterOptionsApplied2[filterOptionIndex2].filterSelection2 = updatedSelectionsTemp2;
        newFilterOptionsApplied2[filterOptionIndex2].unSelectedItems2 = updatedUnSelectedItems2;
        updateFiltersApplied(newFilterOptionsApplied2);
    }

    const updateInclusionSelection = (filterType, updatedInclusionSelection) => {
        const filterOptionIndex = appliedFilters.findIndex((elem) => elem.filterType === filterType);
        if (filterOptionIndex === -1) return;

        let newFilterOptionsApplied = cloneDeep(appliedFilters);
        newFilterOptionsApplied[filterOptionIndex].inclusionSelection = updatedInclusionSelection;

        updateFiltersApplied(newFilterOptionsApplied);
    }

    const removeFilter = (filterType) => {
        const appliedFilterIndex = appliedFilters.findIndex((elem) => elem.filterType === filterType);
        const totalFilterOptionsIndex = totalFilterOptions.findIndex(elem => elem.filterType === filterType);

        if (appliedFilterIndex === -1) return;
        if (totalFilterOptionsIndex === -1) return;

        const newFilterOptionsApplied = [
            ...appliedFilters.slice(0, appliedFilterIndex),
            ...appliedFilters.slice(appliedFilterIndex + 1),
        ];

        const newTotalFilterOptions = totalFilterOptions.map((option, index) =>
            index === totalFilterOptionsIndex ? { ...option, isAdded: false } : option
        );

        updateFiltersApplied(newFilterOptionsApplied);
        updateTotalFilterOptions(newTotalFilterOptions);
    }

    return (
        <div ref={filterButtonRef} className="table-filter-button">
            <div className={`button-container ${isFiltering || isDropdownOpen ? 'active-background' : ''}`}>
                <div className="filter-icon" id={"activated-filter"}></div>
                <div className="filter-label" id={"activated-filter-text"}>
                    Filter: {getNumFilters(appliedFilters)}
                </div>
            </div>
            {   isDropdownOpen &&
                <div className="filter-dropdown">
                    <div className="header-container">
                        <span className="dropdown-title">{headerText}</span>
                        <button className={`clear-button`} disabled={appliedFilters.length > 0 ? false : true}
                            onClick={() => {clearFilters();} }>
                            Clear
                        </button>
                    </div>
                    <hr className="divider" />
                    {appliedFilters.map((filterData, index) => {
                        return (
                            <div key={filterData.filterType} onClick={() => { setFocusedElement(filterData.filterType); } }>
                                <FilterDropdownRow
                                    showConjunctiveDropdown={index !== 0}
                                    disableConjunctiveDropdown={index !== 1}

                                    appliedFilter={filterData}
                                    conjunctionSelection={conjunctionSelection}
                                    customDropdownToggleSelector={customDropdownToggleSelector}
                                    customDropdownItemSelector={customDropdownItemSelector}

                                    updateFilter={updateFilterSelections}
                                    updateFilter2={updateFilterSelections2}
                                    updateInclusion={updateInclusionSelection}
                                    removeFilter={removeFilter}
                                    updateAllConjunction={updateConjunctionSelection}
                                    isFocused={focusedElement === filterData.filterType}
                                    dateFormatID={dateFormatID}

                                    pageIdentifier={pageIdentifier}
                                />
                            </div>
                        );

                    })}

                    <div className="add-filter-dropdown">
                        <Dropdown>
                            <Dropdown.Toggle className="add-filter-title">
                                <span className="add-icon"></span>
                                <span>
                                    Add Filter
                                </span>
                            </Dropdown.Toggle>
                            <Dropdown.Menu className="filter-dropdown-container">
                                {totalFilterOptions.map((filterOption, index) => {
                                    return (
                                        !filterOption.isAdded &&
                                        <div key={filterOption.filterType}>
                                            <Dropdown.Item
                                                className='filter-dropdown-item'
                                                onClick={() => { addFilterRow(filterOption, index); } }>
                                                <span>{filterOption.filterType}</span>
                                            </Dropdown.Item>
                                            <Dropdown.Divider className='filter-dropdown-divider'></Dropdown.Divider>
                                        </div>
                                    );
                                })}
                            </Dropdown.Menu>
                        </Dropdown>
                    </div>
                </div>}
        </div>
    );
}

export default FilterButton;
